import React, { useEffect, useState, useRef } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom'
import AuthLayout from './layouts/AuthLayout'
import HomeLayout from './layouts/HomeLayout'
import Login from './LoginComponents/Login'
import Signup from './LoginComponents/Signup'
import Home from './Home'
import LandingPage from './LoginComponents/LandingPage'
import './theme.css'
import ContactUs from './LoginComponents/ContactUs'
import useAuth from './LoginComponents/useAuth'
import Registration from './LoginComponents/Registration'
import ForgotPassword from './LoginComponents/ForgotPassword'
import UpdatePwd from './LoginComponents/UpdatePwd'
import ResetPwd from './LoginComponents/ResetPwd'
import UpdatePhone from './LoginComponents/UpdatePhone'
import UpdateEmail from './LoginComponents/UpdateEmail'
import SMSVerification from './LoginComponents/SMSVerification'
import UserSettings from './SettingsComponents/UserSettings'
import CompanySettings from './SettingsComponents/CompanySettings'
import UsersManagement from './SettingsComponents/UsersManagement'
import TeamManagement from './SettingsComponents/TeamManagement'
import { verifyToken, getToken, removeToken } from './utils/auth'
import LoadingScreen from './components/LoadingScreen'
import { useUser } from './contexts/UserContext'
import InviteFormComponent from './LoginComponents/InviteFormComponent'
import PhoneVerification from './LoginComponents/PhoneVerification'
import OndsGate from './onds/components/OndsGate'
import { AMPX_DESK } from './onds/deskContext'

function ProtectedRoute({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const { isLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const token = getToken();
      if (!token) {
        setIsAuthenticated(false);
        setIsVerified(true);
        return;
      }

      try {
        const userData = await verifyToken();
        if (userData) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
          removeToken(); // Clear invalid token
        }
      } catch (error) {
        console.error('Auth verification error:', error);
        setIsAuthenticated(false);
      } finally {
        setIsVerified(true);
      }
    };

    checkAuth();

    // Listen for auth token changes
    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener('auth-token-changed', handleAuthChange);
    return () => {
      window.removeEventListener('auth-token-changed', handleAuthChange);
    };
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  // Wait until verification is complete before making a decision
  if (!isVerified) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, userData } = useUser();
  const [authVerified, setAuthVerified] = useState(false);
  const isRedirecting = useRef(false);

  // Check if the current path is an auth page
  const isAuthPage = ['/login', '/signup', '/forgotpwd', '/sms-verification'].includes(location.pathname) || 
                    (location.pathname.startsWith('/signup/') && location.pathname.length > 7) ||
                    location.pathname.startsWith('/signup/invite');

  // Public pages that never require auth and must not trigger login redirects
  const isPublicPage = location.pathname === '/' || location.pathname.startsWith('/onds') || location.pathname.startsWith('/ampx');

  useEffect(() => {
    const verifyInitialAuth = async () => {
      // Skip verification if we're already redirecting
      if (isRedirecting.current) {
        return;
      }

      const token = getToken();
      
      // Check if the current path is for registration verification
      const isRegistrationPath = location.pathname.startsWith('/signup/') && location.pathname.length > 7;
      
      // Include registration paths in auth pages
      const isAuthPage = ['/login', '/signup', '/forgotpwd', '/sms-verification'].includes(location.pathname) || 
                        isRegistrationPath ||
                        location.pathname.includes('/invite');

      const isPublicPath = location.pathname === '/' || location.pathname.startsWith('/onds') || location.pathname.startsWith('/ampx');
      
      // Check if we have an invite URL with query params
      const searchParams = new URLSearchParams(location.search);
      const isInvite = searchParams.get('invite') === 'true';
      const fromEmail = searchParams.get('from') === 'email';
      const key = searchParams.get('key');
      
      // For invite links, save registration token in localStorage
      if (isRegistrationPath && isInvite && fromEmail && key) {
        const regToken = location.pathname.split('/').pop();
        localStorage.setItem('inviteRegToken', regToken);
      }
      
      try {
        if (token) {
          // Verify the token's validity
          const userData = await verifyToken();
          setAuthVerified(true);

          if (!userData) {
            // Token is invalid, remove it
            removeToken();
            if (!isAuthPage && !isPublicPath && !isRedirecting.current) {
              isRedirecting.current = true;
              navigate('/login', { replace: true });
              setTimeout(() => { isRedirecting.current = false; }, 500);
            }
          } else if (isAuthPage && !isRegistrationPath && !isRedirecting.current) {
            // Valid token on auth page, redirect to home
            isRedirecting.current = true;
            navigate('/home', { replace: true });
            setTimeout(() => { isRedirecting.current = false; }, 500);
          }
        } else if (!isAuthPage && !isPublicPath && !isRedirecting.current) {
          // No token and trying to access protected page
          isRedirecting.current = true;
          navigate('/login', { replace: true });
          setTimeout(() => { isRedirecting.current = false; }, 500);
        } else {
          setAuthVerified(true);
        }
      } catch (error) {
        console.error('Initial auth verification error:', error);
        setAuthVerified(true);
        if (!isAuthPage && !isPublicPath && !isRedirecting.current) {
          isRedirecting.current = true;
          navigate('/login', { replace: true });
          setTimeout(() => { isRedirecting.current = false; }, 500);
        }
      }
    };

    verifyInitialAuth();

    // Listen for auth token changes
    const handleAuthChange = () => {
      if (!isRedirecting.current) {
        verifyInitialAuth();
      }
    };

    window.addEventListener('auth-token-changed', handleAuthChange);
    return () => {
      window.removeEventListener('auth-token-changed', handleAuthChange);
    };
  }, [location.pathname, location.search, navigate]);

  // Don't show LoadingScreen on auth pages or public pages, as they don't need authentication
  if (isLoading && !isAuthPage && !isPublicPage) {
    return <LoadingScreen />;
  }

  // No need to wait for verification on auth pages or public pages
  if (!authVerified && !isAuthPage && !isPublicPage) {
    return null;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />

      {/* Secret code-gated ONDS committee desk (public route, own gate) */}
      <Route path="/onds" element={<OndsGate />} />
      <Route path="/ampx" element={<OndsGate desk={AMPX_DESK} />} />
      
      {/* Auth pages */}
      <Route path="/login" element={
        <AuthLayout>
          <Login />
        </AuthLayout>
      } />
      <Route path="/signup" element={
        <AuthLayout>
          <Signup />
        </AuthLayout>
      } />
      <Route path="/signup/:regToken" element={
        <AuthLayout>
          <InviteRedirect />
        </AuthLayout>
      } />
      <Route path="/signup/:regToken/*" element={
        <AuthLayout>
          <Routes>
            <Route path="/" element={<Registration />} />
            <Route path="/invite" element={<InviteFormComponent />} />
            <Route path="/updatephone" element={<UpdatePhone />} />
            <Route path="/updateemail" element={<UpdateEmail />} />
            <Route path="/phoneverification" element={
              <PhoneVerificationWithToken />
            } />
            <Route path="/updatepwd" element={<UpdatePwd />} />
          </Routes>
        </AuthLayout>
      } />
      <Route path="/forgotpwd" element={
        <AuthLayout>
          <ForgotPassword />
        </AuthLayout>
      } />
      <Route path="/updatepwd" element={
        <AuthLayout>
          <UpdatePwd />
        </AuthLayout>
      } />
      <Route path="/resetpwd" element={
        <AuthLayout>
          <ResetPwd />
        </AuthLayout>
      } />
      <Route path="/sms-verification" element={
        <AuthLayout>
          <SMSVerification />
        </AuthLayout>
      } />
      
      {/* Protected pages */}
      <Route path="/home" element={
        <ProtectedRoute>
          <HomeLayout>
            <Home />
          </HomeLayout>
        </ProtectedRoute>
      } />
      <Route path="/user/settings" element={
        <ProtectedRoute>
          <HomeLayout>
            <UserSettings />
          </HomeLayout>
        </ProtectedRoute>
      } />
      <Route path="/company/details" element={
        <ProtectedRoute>
          <HomeLayout>
            <CompanySettings />
          </HomeLayout>
        </ProtectedRoute>
      } />
      <Route path="/company/users" element={
        <ProtectedRoute>
          <HomeLayout>
            <UsersManagement />
          </HomeLayout>
        </ProtectedRoute>
      } />
      <Route path="/company/teams" element={
        <ProtectedRoute>
          <HomeLayout>
            <TeamManagement />
          </HomeLayout>
        </ProtectedRoute>
      } />
    </Routes>
  );
}

// InviteRedirect component to handle direct routing to InviteFormComponent
function InviteRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { regToken } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationData, setRegistrationData] = useState(null);
  const redirectAttempted = useRef(false);
  const API_BASE = import.meta.env.VITE_API_BASE;
  
  useEffect(() => {
    const checkRegistrationState = async () => {
      if (redirectAttempted.current) return;
      
      try {
        console.log("InviteRedirect: Checking registration state for token:", regToken);
        
        // First check the registration state
        const response = await fetch(`${API_BASE}/signup/${regToken}/check`, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        console.log("InviteRedirect: State response status:", response.status);
        
        if (!response.ok) {
          if (response.status === 404) {
            console.log("Registration not found (404)");
            sessionStorage.removeItem('registrationToken');
            redirectAttempted.current = true;
            navigate('/signup', { replace: true });
            return;
          }
          
          if (response.status === 401) {
            console.log("Unauthorized (401), redirecting to login");
            localStorage.clear();
            sessionStorage.removeItem('registrationToken');
            redirectAttempted.current = true;
            navigate('/login', { replace: true });
            return;
          }
          
          throw new Error(`Failed to check registration state: ${response.status}`);
        }
        
        // Parse and handle the registration state
        const stateData = await response.json();
        console.log("InviteRedirect: Registration state:", stateData);
        
        if (!stateData || !stateData.state) {
          throw new Error('Invalid response from server: missing state information');
        }
        
        // Store registration data for direct rendering if needed
        setRegistrationData(stateData);
        sessionStorage.setItem('registrationToken', regToken);
        
        // Get parameters from search query
        const searchParams = new URLSearchParams(location.search);
        const isInvite = searchParams.get('invite') === 'true';
        const fromEmail = searchParams.get('from') === 'email';
        const hasKey = searchParams.get('key');
        
        // Handle routing based on registration state and URL parameters
        switch(stateData.state) {
          case 'InviteForm':
            // Always route to invite form if state requires it
            console.log("InviteRedirect: Redirecting to invite form");
            redirectAttempted.current = true;
            navigate(`/signup/${regToken}/invite${location.search}`, { replace: true });
            break;
            
          case 'PhoneVerification':
            // For phone verification, check if phone is missing first
            try {
              console.log("InviteRedirect: Checking if phone is missing before redirecting");
              const phoneResponse = await fetch(`${API_BASE}/signup/${regToken}/phonev`);
              
              if (phoneResponse.ok) {
                const phoneData = await phoneResponse.json();
                console.log("InviteRedirect: Phone verification check:", phoneData);
                
                if (phoneData.missingPhone === true) {
                  // Skip PhoneVerification and go directly to UpdatePhone
                  console.log("InviteRedirect: Missing phone detected, going directly to updatephone");
                  redirectAttempted.current = true;
                  navigate(`/signup/${regToken}/updatephone`, { replace: true });
                  return;
                }
              }
            } catch (err) {
              console.error("InviteRedirect: Error checking phone status:", err);
              // Continue with normal flow if this check fails
            }
            
            // Route directly to the phone verification component
            console.log("InviteRedirect: Redirecting to phone verification");
            redirectAttempted.current = true;
            navigate(`/signup/${regToken}/phoneverification`, { replace: true });
            break;
            
          case 'MailVerification':
            // Route to mail verification - directly render Registration instead of redirecting
            console.log("InviteRedirect: Detected MailVerification state - loading Registration component");
            setIsLoading(false);
            break;
            
          case 'Login':
            // Registration complete
            console.log("InviteRedirect: Registration complete, redirecting to login");
            sessionStorage.removeItem('registrationToken');
            redirectAttempted.current = true;
            navigate('/login', { replace: true });
            break;
            
          default:
            // Check if this is an invite link with parameters
            if ((isInvite && fromEmail) || (fromEmail && hasKey)) {
              console.log("InviteRedirect: This is an invite link with parameters");
              // Ensure invite=true parameter is added if not present
              if (!isInvite) {
                searchParams.set('invite', 'true');
                redirectAttempted.current = true;
                navigate(`/signup/${regToken}/invite?${searchParams.toString()}`, { replace: true });
              } else {
                redirectAttempted.current = true;
                navigate(`/signup/${regToken}/invite${location.search}`, { replace: true });
              }
            } else {
              // Default case, continue with regular registration
              console.log("InviteRedirect: Default case, continuing with regular registration");
              redirectAttempted.current = true;
              navigate(`/signup/${regToken}`, { replace: true });
            }
        }
      } catch (err) {
        console.error("InviteRedirect: Error checking registration state:", err);
        setError(err.message);
        // Still navigate after a delay to prevent getting stuck
        setTimeout(() => {
          redirectAttempted.current = true;
          navigate(`/signup/${regToken}`, { replace: true });
        }, 1000);
      } finally {
        if (isLoading) {
          // Only set loading to false if we haven't decided to render Registration directly
          if (registrationData?.state !== 'MailVerification') {
            setIsLoading(false);
          }
        }
      }
    };
    
    if (regToken) {
      checkRegistrationState();
    } else {
      redirectAttempted.current = true;
      navigate('/signup', { replace: true });
    }
    
    // Set a safety timeout to prevent getting stuck on loading screen
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log("InviteRedirect: Safety timeout triggered");
        setIsLoading(false);
        
        // If we have registration data, we can still render Registration
        if (!redirectAttempted.current) {
          redirectAttempted.current = true;
          navigate(`/signup/${regToken}`, { replace: true });
        }
      }
    }, 3000);
    
    return () => clearTimeout(timeoutId);
  }, [regToken, location.search, navigate, API_BASE, isLoading, registrationData]);
  
  if (error) {
    return (
      <div className="text-center p-4">
        <p className="text-red-500 mb-3">Error checking registration status</p>
        <button 
          onClick={() => navigate(`/signup/${regToken}`, { replace: true })}
          className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded"
        >
          Continue Registration
        </button>
      </div>
    );
  }
  
  // For MailVerification state, directly render Registration component
  if (!isLoading && registrationData?.state === 'MailVerification') {
    console.log("InviteRedirect: Directly rendering Registration for MailVerification");
    return <Registration />;
  }
  
  return <LoadingScreen />;
}

// Helper component to extract the regToken from URL parameters and pass it to PhoneVerification
function PhoneVerificationWithToken() {
  const { regToken } = useParams();
  const navigate = useNavigate();
  
  const handleRegistrationFailed = () => {
    sessionStorage.removeItem('registrationToken');
    navigate('/signup');
  };

  return (
    <PhoneVerification 
      regToken={regToken} 
      onRegistrationFailed={handleRegistrationFailed} 
    />
  );
}
